import * as THREE from 'three';

// Distant planets, moons, and nebulae. Everything opts out of fog so the sky
// stays crisp, and uses emissive materials so each body reads against the
// starfield without needing the sun to reach it.

const distantBodies = [];   // internal — updated via updateCelestialBodies

function makeDistantPlanet(opts) {
  const group = new THREE.Group();

  const body = new THREE.Mesh(
    new THREE.SphereGeometry(opts.r, 36, 28),
    new THREE.MeshStandardMaterial({
      color: opts.color,
      emissive: opts.emissive,
      emissiveIntensity: opts.emissiveIntensity ?? 0.5,
      roughness: 0.95,
      metalness: 0,
      flatShading: opts.flat ?? false,
      fog: false,
    }),
  );
  group.add(body);

  if (opts.rings) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(opts.r * 1.45, opts.r * (opts.ringOuter ?? 2.2), 96),
      new THREE.MeshBasicMaterial({
        color: opts.ringColor ?? 0xd8c098,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: opts.ringOpacity ?? 0.55,
        depthWrite: false,
        fog: false,
      }),
    );
    // Tilted disc instead of edge-on line
    ring.rotation.x = Math.PI / 2 + (opts.ringTiltX ?? 0.35);
    ring.rotation.z = opts.ringTiltZ ?? 0.4;
    group.add(ring);
  }

  group.position.fromArray(opts.pos);
  group.userData.spinAxis = new THREE.Vector3(
    Math.random() - 0.5, 1, Math.random() - 0.5,
  ).normalize();
  group.userData.spinRate =
    (0.02 + Math.random() * 0.06) * (Math.random() < 0.5 ? 1 : -1);
  distantBodies.push(group);
  return group;
}

function makeNebula({ pos, r, color, strength = 0.22 }) {
  const material = new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    uniforms: {
      uColor:    { value: new THREE.Color(color) },
      uStrength: { value: strength },
    },
    vertexShader: /* glsl */`
      varying vec3 vN;
      void main() {
        vN = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      uniform float uStrength;
      varying vec3 vN;
      void main() {
        float f = pow(1.0 - abs(vN.z), 2.4);
        gl_FragColor = vec4(uColor, f * uStrength);
      }`,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(r, 28, 20), material);
  mesh.position.fromArray(pos);
  return mesh;
}

// Hand-picked spread: ringed gas giant, Neptune, red desert, violet rogue,
// pale icy, tiny moon, green ringed companion, yellow speck.
const PLANET_CONFIGS = [
  { pos: [240,   25,   80], r: 10,  color: 0xd6b684, emissive: 0x4a3820, rings: true,  ringColor: 0xe6ceae, ringTiltX:  0.35, ringTiltZ: 0.55, ringOuter: 2.3 },
  { pos: [-220,  80, -140], r: 6.5, color: 0x7aa8d8, emissive: 0x1e3858 },
  { pos: [130,  -95,  250], r: 4.2, color: 0xd46a3a, emissive: 0x60200f },
  { pos: [-280, -30,  -80], r: 5.0, color: 0x9d7ad0, emissive: 0x34204c },
  { pos: [170,  160, -100], r: 3.8, color: 0xdedde4, emissive: 0x404858, flat: true },
  { pos: [-90,  200,  170], r: 2.4, color: 0xd8b092, emissive: 0x5a3a20 },
  { pos: [300, -130,  -40], r: 8,   color: 0x58a888, emissive: 0x184030, rings: true,  ringColor: 0x9ed0b0, ringTiltX: -0.5, ringTiltZ: 0.2,  ringOuter: 1.9, ringOpacity: 0.35 },
  { pos: [-60, -180, -220], r: 3.2, color: 0xe8c858, emissive: 0x5a4010 },
];

const NEBULA_CONFIGS = [
  { pos: [-320, -120,  220], r: 70, color: 0x8844c8, strength: 0.22 },
  { pos: [ 300,  200, -260], r: 85, color: 0x3a88c8, strength: 0.18 },
  { pos: [ -60, -250, -320], r: 55, color: 0xc84488, strength: 0.20 },
];

export function createCelestialBodies(scene) {
  for (const cfg of PLANET_CONFIGS) scene.add(makeDistantPlanet(cfg));
  for (const cfg of NEBULA_CONFIGS) scene.add(makeNebula(cfg));
}

// Gentle self-rotation so the distant bodies don't feel static.
export function updateCelestialBodies(dt) {
  for (const b of distantBodies) {
    b.rotateOnAxis(b.userData.spinAxis, b.userData.spinRate * dt);
  }
}
